<template>
  <div class="crm-prospect-selector">
    <div class="d-flex align-items-center flex-gap-1">
      <v-select
        class="flex-grow-1"
        label="label"
        placeholder="Select or search prospect..."
        v-model="selectedProspect"
        :options="prospects"
        :filterable="false"
        :loading="loadingProspects"
        :disabled="disabled"
        clearable
        @search="onSearchProspect"
      >
        <template v-slot:option="prospect">
          <strong>{{ prospect.name || `Prospect #${prospect.id}` }}</strong>
          <div class="small text-muted">{{ prospectAddressLabel(prospect) }}</div>
        </template>
        <template slot="selected-option" slot-scope="prospect">
          <span>{{ prospect.name || prospect.label || `Prospect #${prospect.id}` }}</span>
        </template>
      </v-select>

      <CButton
        v-if="allowCreate"
        type="button"
        color="secondary"
        variant="outline"
        :disabled="disabled"
        @click="showCreateForm = !showCreateForm"
      >
        <CIcon :name="showCreateForm ? 'cil-x' : 'cil-plus'" />
      </CButton>
    </div>

    <CCollapse :show="showCreateForm">
      <div class="prospect-create border rounded p-3 mt-2">
        <CRow>
          <CCol md="6">
            <CInput
              label="Prospect name"
              placeholder="Restaurant/prospect name"
              :value.sync="form.name"
            />
          </CCol>

          <CCol md="6">
            <CInput
              label="Address"
              placeholder="Street and number"
              :value.sync="form.address"
            />
          </CCol>
        </CRow>

        <CRow>
          <CCol md="3">
            <div class="form-group">
              <label>Country</label>
              <v-select
                label="label"
                placeholder="Country"
                v-model="form.country_id"
                :options="countrySelectOptions"
                :reduce="country => country.value"
                :searchable="false"
                clearable
                @input="onCountryChange"
              />
            </div>
          </CCol>

          <CCol md="3">
            <div class="form-group">
              <label>Province</label>
              <v-select
                label="label"
                placeholder="Province"
                v-model="form.province_id"
                :options="provinceOptions"
                :reduce="province => province.id"
                :loading="loadingProvinces"
                :disabled="!form.country_id"
                :searchable="false"
                clearable
                @input="onProvinceChange"
              />
            </div>
          </CCol>

          <CCol md="6">
            <div class="form-group">
              <label>Postcode</label>
              <v-select
                label="label"
                placeholder="Type to search postcodes..."
                v-model="selectedPostcode"
                :filterable="false"
                :options="postcodeOptions"
                :loading="loadingPostcodes"
                :disabled="!form.province_id"
                clearable
                @search="onSearchPostcode"
                @input="onPostcodeChange"
              >
                <template v-slot:option="zipCode">
                  <strong>{{ zipCode.zcZip }}</strong>
                  <span>- {{ zipCode.zcCity }}</span>
                </template>
                <template slot="selected-option" slot-scope="zipCode">
                  <strong class="text-dark mr-1">{{ zipCode.zcZip }}</strong>
                  <span>{{ zipCode.zcCity }}</span>
                </template>
                <span slot="no-options">
                  {{ form.province_id ? 'Type to search postcodes.' : 'Select province first.' }}
                </span>
              </v-select>
            </div>
          </CCol>
        </CRow>

        <CRow>
          <CCol md="6">
            <CInput
              label="City"
              placeholder="City"
              :value.sync="form.city"
              readonly
            />
          </CCol>
        </CRow>

        <CRow>
          <CCol md="4">
            <CInput label="Phone" placeholder="Phone" :value.sync="form.phone" />
          </CCol>
          <CCol md="4">
            <CInput label="Email" placeholder="Email" :value.sync="form.email" />
          </CCol>
          <CCol md="4">
            <CInput label="Website" placeholder="Website" :value.sync="form.website" />
          </CCol>
        </CRow>

        <CButton
          type="button"
          color="primary"
          size="sm"
          :disabled="savingProspect || !canSaveProspect"
          @click="saveProspect"
        >
          <CSpinner v-if="savingProspect" size="sm" class="mr-1" />
          <CIcon v-else name="cil-save" />
          <span class="ml-1">Save Prospect</span>
        </CButton>
      </div>
    </CCollapse>
  </div>
</template>

<script>
export default {
  name: "CrmProspectSelector",
  props: {
    value: {
      type: [Number, String],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    allowCreate: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loadingProspects: false,
      loadingPostcodes: false,
      loadingProvinces: false,
      savingProspect: false,
      showCreateForm: false,
      prospects: [],
      provinceOptions: [],
      postcodeOptions: [],
      selectedProspect: null,
      selectedPostcode: null,
      form: this.emptyForm(),
    };
  },
  computed: {
    countrySelectOptions() {
      return [
        ...this.authCountries,
      ];
    },
    hasMinimumProspectInfo() {
      return !!(
        this.form.name ||
        this.form.address ||
        this.form.postcode ||
        this.form.city
      );
    },
    hasValidPostcodeSelection() {
      return !this.form.postcode || !!this.selectedPostcode;
    },
    canSaveProspect() {
      return this.hasMinimumProspectInfo && this.hasValidPostcodeSelection;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        if (!value) {
          this.selectedProspect = null;
          return;
        }

        if (this.selectedProspect && `${this.selectedProspect.id}` === `${value}`) {
          return;
        }

        this.loadSelectedProspect(value);
      },
    },
    selectedProspect(prospect) {
      this.$emit('input', prospect ? prospect.id : null);
      this.$emit('selected', prospect || null);
    },
  },
  mounted() {
    this.getProspects();
    this.getProvinces();
  },
  methods: {
    emptyForm() {
      return {
        name: '',
        address: '',
        postcode: '',
        city: '',
        country_id: this.$store.state.countryId || null,
        province_id: null,
        phone: '',
        email: '',
        website: '',
      };
    },
    onSearchProspect(search, loading) {
      loading(true);
      this.getProspects(search).finally(() => loading(false));
    },
    async getProspects(search = '') {
      this.loadingProspects = true;

      await this.$axios
        .get(this.$backend.CRM_PROSPECTS.GET_ALL, {
          params: {
            q: search || null,
            limit: 30,
          },
        })
        .then((response) => {
          const prospects = response.data.data || response.data || [];
          this.prospects = prospects.map(this.buildProspectOption);
        })
        .catch(() => {
          this.prospects = [];
        })
        .finally(() => {
          this.loadingProspects = false;
        });
    },
    async loadSelectedProspect(id) {
      const existing = this.prospects.find((prospect) => `${prospect.id}` === `${id}`);
      if (existing) {
        this.selectedProspect = existing;
        return;
      }

      await this.$axios
        .get(this.$backend.CRM_PROSPECTS.SHOW.replace('{id}', id))
        .then((response) => {
          const prospect = this.buildProspectOption(response.data);
          this.prospects = [prospect, ...this.prospects.filter((item) => `${item.id}` !== `${prospect.id}`)];
          this.selectedProspect = prospect;
        });
    },
    async getProvinces() {
      if (!this.form.country_id) {
        this.provinceOptions = [];
        return;
      }

      this.loadingProvinces = true;
      await this.$axios
        .get(this.$backend.CRM_MESSAGES.GEO_PROVINCES, {
          params: {
            country: this.form.country_id,
          },
        })
        .then((response) => {
          this.provinceOptions = (response.data || [])
            .filter((province) => province.id)
            .map((province) => ({
              ...province,
              label: province.label || province.name_nl || province.name_fr || province.code,
            }));
        })
        .catch(() => {
          this.provinceOptions = [];
        })
        .finally(() => {
          this.loadingProvinces = false;
        });
    },
    onCountryChange() {
      this.form.province_id = null;
      this.clearPostcodeSelection();
      this.getProvinces();
    },
    onProvinceChange() {
      this.clearPostcodeSelection();
    },
    clearPostcodeSelection() {
      this.selectedPostcode = null;
      this.postcodeOptions = [];
      this.form.postcode = '';
      this.form.city = '';
    },
    onSearchPostcode(search, loading) {
      if (!this.form.country_id || !this.form.province_id || !search) {
        this.postcodeOptions = [];
        return;
      }

      loading(true);
      this.searchPostcodes(search).finally(() => loading(false));
    },
    async searchPostcodes(search) {
      this.loadingPostcodes = true;

      await this.$axios
        .get(this.$backend.ZIP_CODES.SEARCH, {
          params: {
            q: search,
            country: this.form.country_id,
            'province-id': this.form.province_id,
            fields: 'zcZip,zcCity',
          },
        })
        .then((response) => {
          this.postcodeOptions = (response.data || []).map(this.buildPostcodeOption);
        })
        .catch(() => {
          this.postcodeOptions = [];
        })
        .finally(() => {
          this.loadingPostcodes = false;
        });
    },
    onPostcodeChange(zipCode) {
      if (!zipCode) {
        this.form.postcode = '';
        this.form.city = '';
        return;
      }

      this.form.postcode = zipCode.zcZip;
      this.form.city = zipCode.zcCity || '';
      this.form.country_id = zipCode.zcCountryId || this.form.country_id;
      this.form.province_id = zipCode.province_id || this.form.province_id;
    },
    async saveProspect() {
      if (!this.canSaveProspect || this.savingProspect) return;

      this.savingProspect = true;
      await this.$axios
        .post(this.$backend.CRM_PROSPECTS.STORE, this.form)
        .then((response) => {
          const prospect = this.buildProspectOption(response.data.data);
          this.prospects = [prospect, ...this.prospects.filter((item) => `${item.id}` !== `${prospect.id}`)];
          this.selectedProspect = prospect;
          this.form = this.emptyForm();
          this.selectedPostcode = null;
          this.postcodeOptions = [];
          this.showCreateForm = false;
          this.getProvinces();
          this.$toast.success(response.data.message);
          this.$emit('created', prospect);
        })
        .catch((error) => {
          this.$toast.error(error.response?.data?.message || 'CRM prospect could not be saved.');
        })
        .finally(() => {
          this.savingProspect = false;
        });
    },
    buildProspectOption(prospect) {
      return {
        ...prospect,
        label: prospect.label || this.prospectAddressLabel(prospect) || prospect.name || `Prospect #${prospect.id}`,
      };
    },
    buildPostcodeOption(zipCode) {
      return {
        ...zipCode,
        label: `${zipCode.zcZip} - ${zipCode.zcCity || ''}`,
      };
    },
    prospectAddressLabel(prospect) {
      return [
        prospect.name,
        prospect.address,
        [prospect.postcode, prospect.city].filter(Boolean).join(' '),
      ].filter(Boolean).join(', ');
    },
  },
};
</script>

<style scoped>
.prospect-create {
  background: #f8f9fa;
}
</style>
